import React, { useState, useEffect } from 'react';
import { FiPlus, FiPackage, FiDownload, FiAlertTriangle } from 'react-icons/fi';
import { api } from '../api';
import { useMe } from '../App';
import { findPerm } from '../perm';
import { exportExcel } from '../utils/exportExcel';
import InventoryDetails from './InventoryDetails';
import './Inventory.css';

export default function Store() {
  const me = useMe();
  const perm = findPerm(me.modules, 'store');
  const [rows, setRows] = useState(null);
  const [search,setSearch]=useState('');
  const [brandFilter,setBrandFilter]=useState('');
  const [detailId,setDetailId]=useState(null);
  const [loadError,setLoadError]=useState('');
  const [brands, setBrands] = useState([]);
  const [modal, setModal] = useState(null);
  const [moveModal, setMoveModal] = useState(null);
  const [disposeFor, setDisposeFor] = useState(null);
  const [lowOnly, setLowOnly] = useState(false);
  const [toast, setToast] = useState('');

  async function load() {setLoadError(''); try { setRows(await api('/store/products')); } catch (e) { setLoadError(e.message); } }
  useEffect(() => { load(); }, []);
  useEffect(() => { api('/brands/options').then(setBrands).catch(() => {}); }, []);
  function flash(m) { setToast(m); setTimeout(() => setToast(''), 2600); }

  const shown = (rows || []).filter(p => (!lowOnly||p.low_stock) && (!brandFilter||String(p.brand_id)===brandFilter) && (p.name+' '+(p.specifications||'')).toLowerCase().includes(search.toLowerCase()));
  const lowCount = (rows || []).filter((p) => p.low_stock).length;

  function doExport() {
    exportExcel('Store_stock', [
      { label: 'Product', key: 'name' },
      { label: 'Brand', value: (p) => p.brand_name || '' },
      { label: 'Specifications', value: (p) => p.specifications || '' },
      { label: 'Total', key: 'total_stock' },
      { label: 'Reserved', key: 'reserved_stock' },
      { label: 'Issuable', key: 'issuable_stock' },
      { label: 'Damage', key: 'damage_stock' },
      { label: 'Reorder level', key: 'reorder_level' },
      { label: 'Status', value: (p) => (p.low_stock ? 'LOW — reorder' : 'OK') },
    ], shown);
  }

  return (
    <div className="inventory-page">
      <div className="mod-head">
        <div className="titles">
          <div className="page-eyebrow">Inventory</div>
          <h1>Products & stock</h1>
          <div className="count">
            {rows ? `${rows.length} product${rows.length !== 1 ? 's' : ''}` : 'Loading…'}
            {lowCount > 0 && <span className="pill" style={{ background: '#FBEEEA', color: '#B4432E', marginLeft: 8 }}>{lowCount} low stock</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-sm" onClick={doExport} disabled={!rows || rows.length === 0}><FiDownload /> Export to Excel</button>
          {perm.can_create && <button className="btn btn-dark" onClick={() => setModal({ mode: 'add', product: {} })}><FiPlus /> Add product</button>}
        </div>
      </div>

      <div className="inventory-metrics">{[['Products',rows?.length||0],['Low stock',lowCount],['With reservations',(rows||[]).filter(p=>p.reserved_stock>0).length],['Out of stock',(rows||[]).filter(p=>p.issuable_stock<=0).length]].map(([label,value])=><div key={label}><span>{label}</span><strong>{value}</strong></div>)}</div>
      {loadError&&<div className="inline-error" role="alert">{loadError} <button className="btn" onClick={load}>Retry</button></div>}
      <div className="inventory-filters">
        <label>Search <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Product or specification"/></label>
        <label>Brand <select value={brandFilter} onChange={e=>setBrandFilter(e.target.value)}><option value="">All brands</option>{brands.map(b=><option key={b.id} value={b.id}>{b.label}</option>)}</select></label>
        <button className={'btn btn-sm' + (lowOnly ? ' btn-dark' : '')} onClick={() => setLowOnly((v) => !v)}>
          <FiAlertTriangle /> {lowOnly ? 'Showing low stock' : 'Low stock only'}
        </button>
      </div>

      <div className="table-card inventory-table-scroll">
        {!rows ? <div className="empty">Loading…</div> :
         shown.length === 0 ? <div className="empty"><h3>{lowOnly ? 'No matching low-stock products' : 'No matching products'}</h3><p>{lowOnly ? 'All items are above their reorder level.' : 'Add your first inventory product.'}</p></div> :
          <table className="data">
            <thead><tr>
              <th>Product</th><th>Brand</th>
              <th style={{ textAlign: 'right' }}>Total</th>
              <th style={{ textAlign: 'right' }}>Reserved</th>
              <th style={{ textAlign: 'right' }}>Issuable</th>
              <th style={{ textAlign: 'right' }}>Damage</th>
              <th style={{ textAlign: 'right' }}>Reorder</th>
              <th>Supply</th>
              {perm.can_edit && <th style={{ textAlign: 'right' }}>Actions</th>}
            </tr></thead>
            <tbody>
              {shown.map((p) => (
                <tr key={p.id} style={p.low_stock ? { background: '#FDF6F4' } : null}>
                  <td style={{ fontWeight: 600 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {p.image_url
                        ? <img src={p.image_url} alt="" style={{ width: 34, height: 34, borderRadius: 7, objectFit: 'cover', border: '1px solid var(--line)' }} />
                        : <span style={{ width: 34, height: 34, borderRadius: 7, background: '#F3EFE7', display: 'grid', placeItems: 'center', color: 'var(--brass)' }}><FiPackage /></span>}
                      <div>
                        <button type="button" className="inventory-name" onClick={()=>setDetailId(p.id)}>{p.name}</button>
                        {p.low_stock && <span className="pill" style={{ background: '#FBEEEA', color: '#B4432E', marginLeft: 8, fontSize: 11 }}>low</span>}
                        {p.specifications && <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 400 }}>{p.specifications.slice(0, 60)}</div>}
                      </div>
                    </div>
                  </td>
                  <td style={{ color: 'var(--muted)' }}>{p.brand_name || '—'}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{p.total_stock}</td>
                  <td style={{ textAlign: 'right' }}>
                    {p.reserved_stock > 0 ? <span className="pill" style={{ background: '#FDF3E3', color: '#A87B2D' }}>{p.reserved_stock}</span> : <span style={{ color: 'var(--muted)' }}>0</span>}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 600, color: p.issuable_stock <= 0 ? '#B4432E' : (p.low_stock ? '#A87B2D' : '#3B7A4E') }}>{p.issuable_stock}</td>
                  <td style={{ textAlign: 'right' }}>{p.damage_stock > 0 ? <span className="pill" style={{ background: '#FBEEEA', color: '#B4432E' }}>{p.damage_stock}</span> : <span style={{ color: 'var(--muted)' }}>0</span>}</td>
                  <td style={{ textAlign: 'right', color: 'var(--muted)' }}>{p.reorder_level > 0 ? p.reorder_level : '—'}</td>
                  <td><span className="pill" style={p.supply_method === 'make' ? { background: '#F0EAF5', color: '#6B4B7A' } : { background: '#EAF0F8', color: '#3A6A96' }}>{p.supply_method === 'make' ? 'Make' : 'Buy'}</span></td>
                  {perm.can_edit && (
                    <td><div className="row-actions">
                      <button className="btn btn-sm" onClick={() => setMoveModal({ product: p, type: 'purchase' })}>+ Stock</button>
                      <button className="btn btn-sm btn-danger" onClick={() => setMoveModal({ product: p, type: 'damage' })}>Damage</button>
                      {p.damage_stock > 0 && <button className="btn btn-sm" onClick={() => setDisposeFor(p)}>Dispose</button>}
                      <button className="btn btn-sm" onClick={() => setModal({ mode: 'edit', product: p })}>Edit</button>
                    </div></td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>}
      </div>

      {detailId && <InventoryDetails productId={detailId} onClose={()=>setDetailId(null)}/>}
      {modal && <ProductModal {...modal} brands={brands} onClose={() => setModal(null)} onSaved={(m) => { setModal(null); flash(m); load(); }} />}
      {moveModal && <MovementModal {...moveModal} onClose={() => setMoveModal(null)} onSaved={(m) => { setMoveModal(null); flash(m); load(); }} />}
      {disposeFor && <DamageDisposeModal product={disposeFor} onClose={() => setDisposeFor(null)} onSaved={(m) => { setDisposeFor(null); flash(m); load(); }} />}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

function DamageDisposeModal({ product, onClose, onSaved }) {
  const [action, setAction] = useState('repair');
  const [qty, setQty] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState(''); const [busy, setBusy] = useState(false);
  const dmg = product.damage_stock || 0;
  async function save() {
    setError(''); setBusy(true);
    try {
      await api(`/store/products/${product.id}/damage-dispose`, { method: 'POST', body: { action, qty, note } });
      onSaved(action === 'repair' ? 'Repaired to good stock' : action === 'return' ? 'Returned to supplier' : 'Written off');
    } catch (e) { setError(e.message); setBusy(false); }
  }
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Dispose damaged stock</h2>
        <p className="page-desc" style={{ margin: '0 0 14px', fontSize: 13 }}>{product.name} · {dmg} in damage store</p>
        <div className="field"><label>Action</label>
          <select value={action} onChange={(e) => setAction(e.target.value)}>
            <option value="repair">Repair → back to good stock</option>
            <option value="return">Return to supplier</option>
            <option value="writeoff">Write off (scrap)</option>
          </select></div>
        <div className="field"><label>Quantity</label>
          <input type="number" value={qty} autoFocus onChange={(e) => setQty(e.target.value)} placeholder={`Up to ${dmg}`} /></div>
        <div className="field"><label>Note (optional)</label>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={action === 'return' ? 'Supplier / reference' : 'Reason'} /></div>
        {error && <div className="inline-error">{error}</div>}
        <div className="modal-actions">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-dark" onClick={save} disabled={busy || !qty}>{busy ? 'Saving…' : 'Confirm'}</button>
        </div>
      </div>
    </div>
  );
}

function ProductModal({ mode, product, brands, onClose, onSaved }) {
  const [f, setF] = useState({
    name: product.name || '', specifications: product.specifications || '',
    image_url: product.image_url || '', brand_id: product.brand_id || '', opening_stock: '',
    reorder_level: product.reorder_level != null ? product.reorder_level : '',
    supply_method: product.supply_method || 'buy',
  });
  const [error, setError] = useState(''); const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  async function save() {
    setError(''); setBusy(true);
    try {
      if (mode === 'add') { await api('/store/products', { method: 'POST', body: f }); onSaved('Product added'); }
      else { await api('/store/products/' + product.id, { method: 'PUT', body: f }); onSaved('Product updated'); }
    } catch (e) { setError(e.message); setBusy(false); }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 500 }} onClick={(e) => e.stopPropagation()}>
        <h2>{mode === 'add' ? 'Add product' : 'Edit product'}</h2>
        <div className="field"><label>Product name</label>
          <input value={f.name} autoFocus onChange={set('name')} placeholder="e.g. Danglers, Standee, Head Caps" /></div>
        <div className="field"><label>Specifications</label>
          <input value={f.specifications} onChange={set('specifications')} placeholder="Size, material, print details…" /></div>
        <div className="grid2">
          <div className="field"><label>Brand</label>
            <select value={f.brand_id} onChange={set('brand_id')}>
              <option value="">— None —</option>
              {brands.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
            </select></div>
          <div className="field"><label>Reorder level</label>
            <input type="number" value={f.reorder_level} onChange={set('reorder_level')} placeholder="Alert when issuable ≤ this" /></div>
        </div>
        <div className="field"><label>Supply method</label>
          <select value={f.supply_method} onChange={set('supply_method')}>
            <option value="buy">Buy — replenish via Purchase Request</option>
            <option value="make">Make — replenish via Assembly</option>
          </select></div>
        {mode === 'add' && <div className="field"><label>Opening stock</label>
          <input type="number" value={f.opening_stock} onChange={set('opening_stock')} placeholder="0" /></div>}
        <div className="field"><label>Image URL (optional)</label>
          <input value={f.image_url} onChange={set('image_url')} placeholder="https://…" /></div>
        {error && <div className="inline-error">{error}</div>}
        <div className="modal-actions">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-dark" onClick={save} disabled={busy || !f.name.trim()}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

function MovementModal({ product, type, onClose, onSaved }) {
  const [qty, setQty] = useState(''); const [note, setNote] = useState('');
  const [error, setError] = useState(''); const [busy, setBusy] = useState(false);
  const label = type === 'purchase' ? 'Receive stock manually' : 'Record damage';

  async function save() {
    setError(''); setBusy(true);
    try {
      await api(`/store/products/${product.id}/movement`, { method: 'POST', body: { movement_type: type, qty, note } });
      onSaved(type === 'purchase' ? 'Stock added' : 'Damage recorded');
    } catch (e) { setError(e.message); setBusy(false); }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{label}</h2>
        <p className="page-desc" style={{ margin: '0 0 14px', fontSize: 13 }}>{product.name} · current total {product.total_stock}</p>
        {type==='purchase' && <p className="inventory-muted">Use this for receipts without a purchase request. Receiving a purchase request already adds its stock.</p>}
        <div className="field"><label>Quantity</label>
          <input type="number" value={qty} autoFocus onChange={(e) => setQty(e.target.value)} placeholder="0" /></div>
        <div className="field"><label>Note (optional)</label>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={type === 'purchase' ? 'Supplier / invoice ref' : 'Reason for damage'} /></div>
        {error && <div className="inline-error">{error}</div>}
        <div className="modal-actions">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-dark" onClick={save} disabled={busy || !Number.isFinite(Number(qty)) || Number(qty)<=0}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
